import React from 'react';

import PageHeader from '../components/page-header';
import TextBox from '../components/textbox';
import CustomCombobox from '../components/combobox';
import CustomCheckbox from '../components/checkbox';
import ButtonFilled from '../components/button-filled';
import CustomAutoComplete from '../components/autocomplete';
import { selectOrderItemAmount } from '../redux/orderItemAmountSlice';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik, useFormik } from 'formik';
import * as Yup from 'yup';
import {selectOrderInfo, setOrderInfo } from '../redux/orderInfoSlice';
import { UpdateStateOrderInfo } from '../services/last-state-service';
import { countries } from 'countries-list';

const excludedCountries = [
  'Afghanistan',
  'Belarus',
  'Bhutan',
  'Brunei',
  'Cook Islands',
  'East Timor',
  'Fiji',
  'Gaza Strip',
  'Kiribati',
  'Laos',
  'Marshall Islands',
  'Micronesia',
  'Myanmar (Burma)',
  'Niue',
  'Palau',
  'Papua New Guinea',
  'Russia',
  'Solomon Islands',
  'Syria',
  'Tonga',
  'Tuvalu',
  'Ukraine',
  'Vanuatu',
  'Wallis and Futuna',
  'Syrian Arab Republic',
  'Yemen',
];

const sizes = [
  'XS',
  'S',
  'M',
  'L',
  'XL',
  '2XL',
  '3XL'
];

const shippingValidationSchema = Yup.object({
  fullName: Yup.string().required('Full Name is required').max(50, 'Phone Number must be maximum 50 characters'),
  email: Yup.string().email('Invalid email address').required('Email is required').max(100, 'Email must be maximum 100 characters'),
  shippingAddress: Yup.string().required('Shipping Address is required').max(50, 'Shipping Address must be maximum 50 characters'),
  shippingAddressCont: Yup.string().max(50, 'Shipping Address Cont must be maximum 50 characters'),
  zipCode: Yup.string().required('ZIP Code is required').max(15, 'ZIP Code must be maximum 15 characters'),
  city: Yup.string().required('City is required').max(50, 'City must be maximum 50 characters'),
  state: Yup.string().required('State is required').max(50, 'State must be maximum 50 characters'),
  country: Yup.string().required('Country is required').max(50, 'Country must be maximum 50 characters'),
  prefixPhoneNumber: Yup.string().required('Prefix is required').max(6, 'Prefix must be maximum 6 characters'),
  phoneNumber: Yup.string().required('Phone Number is required')
    .min(4, 'Phone Number must be at least 4 characters')
    .max(20, 'Phone Number must be maximum 20 characters')
    .matches(/^[0-9-]+$/, 'Phone Number must only contain numbers'),
  sizes: Yup.array().of(Yup.string().required('Size is required')).required('At least one size is required'),
});

const confirmData = {
  agreeTerms: false,
  confirmSizing: false,
};

const confirmDataValidationSchema = Yup.object().shape({
  agreeTerms: Yup.bool().oneOf([true], 'You must agree to the terms'),
  confirmSizing: Yup.bool().oneOf([true], 'You must confirm sizing awareness'),
});

const ShippingInformationPage = ({ handleNext, handleGoQuiz, handleGoHomepage }) => {
  const orderItemCount = useSelector(selectOrderItemAmount);
  const shippingInfo = {
    fullName: '',
    email: '',
    shippingAddress: '',
    shippingAddressCont: '',
    zipCode: '',
    city: '',
    state: '',
    country: '',
    prefixPhoneNumber: '',
    phoneNumber: '',
    sizes: Array(orderItemCount).fill(''),
  };

  const savedShippingInfo = useSelector(selectOrderInfo);
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: savedShippingInfo.phoneNumber ? savedShippingInfo : shippingInfo,
    validationSchema: shippingValidationSchema,
    onSubmit: async (values, actions) => {
      dispatch(setOrderInfo(values));
      await UpdateStateOrderInfo(values);
    },
  });
  
  const checkBoxesFormik = useFormik({
    initialValues: confirmData,
    validationSchema: confirmDataValidationSchema,
    validateOnChange: false,
    onSubmit: (values) => {
    }
  });

  const onSubmitShippingInfo = async () => {
    const shippingInfoErrors = await formik.validateForm(formik.values);
    const checkboxErrors = await checkBoxesFormik.validateForm(checkBoxesFormik.values);

    if (Object.keys(shippingInfoErrors).length === 0 
        && Object.keys(checkboxErrors).length === 0) {
      await formik.submitForm();
      handleNext();
    }
    else {
      const touchedFields = Object.keys(formik.values).reduce((acc, key) => {
        if (Array.isArray(formik.values[key])) {
          const arrayTouched = formik.values[key].reduce((arrAcc, _, index) => {
            arrAcc[index] = true;
            return arrAcc;
          }, []);
          acc[key] = arrayTouched;
        } else {
          // For non-array fields, touch the entire field
          acc[key] = true;
        }
        return acc;
      }, {});
      
      
      formik.setTouched({ ...formik.touched, ...touchedFields });
      checkBoxesFormik.setTouched(Object.keys(checkBoxesFormik.values).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
    }
  }


  const filteredCountries = Object.entries(countries)
    .filter(([code, country]) => !excludedCountries.includes(country.name))
    .map(([code, country]) => country.name)
    .sort();

  return (
    <section className='page-container'>
      <div className='order-page shipping-info-stage'>

        <div className='center-items-horizontal'>
          <PageHeader currentStepNumber={3} backToHomepage={handleGoHomepage} />

          <div className='shipping-info-content'>
            <div className='disclaimers'>
              <p className='shirt-size-disclaimer'>
                Make sure you go over <a href="https://www.lvlcs.io/size-fit" target="_blank" rel="noreferrer"><span>size chart</span></a> for the jersey before submitting your info.
              </p>
              <br/>
              <p className='personal-data-disclaimer'>
                All your personal information is only used for shipping purposes only.
              </p>
            </div>

            <Formik {...formik}>
              <div className='shipping-data-input'>
                <Form>
                  <TextBox 
                    value={formik.values.fullName} 
                    onChange={(e) => { 
                      formik.handleChange(e); 
                      formik.setFieldTouched("fullName", false)
                    }} 
                    label={'Recipient Full Name'} 
                    id="fullName" 
                    name="fullName"              
                    isStatusDisplayed={!!formik.touched.fullName} 
                    error={formik.errors.fullName} 
                  />
                  <TextBox 
                    value={formik.values.email} 
                    onChange={(e) => { 
                      formik.handleChange(e); 
                      formik.setFieldTouched("email", false);                      
                    }} 
                    label={'Email'} 
                    customStyles={{margin: '16px 0 0 0'}}
                    id="email" 
                    name="email"
                    isStatusDisplayed={!!formik.touched.email} 
                    error={formik.errors.email}
                  />                  
                  <CustomAutoComplete
                    autoSelectValue={formik.values.shippingAddress}
                    handleChange={(_, value) => formik.setFieldValue("shippingAddress", value)}
                    isStatusDisplayed={!!formik.touched.shippingAddress}
                    error={formik.errors.shippingAddress}
                    id="shippingAddress" 
                    name="shippingAddress"
                  />              
                  <TextBox 
                    value={formik.values.shippingAddressCont} 
                    onChange={(e) => { 
                      formik.handleChange(e); 
                      formik.setFieldTouched("shippingAddressCont", false)
                    }} 
                    label={'Shipping Address Cont.  (apt, suite, building)'} 
                    customStyles={{margin: '16px 0 0 0'}} 
                    id="shippingAddressCont" 
                    name="shippingAddressCont" 
                    isStatusDisplayed={!!formik.touched.shippingAddressCont} 
                    error={formik.errors.shippingAddressCont}
                  />  

                  <div className='multiple-row-input'>
                    <TextBox 
                      value={formik.values.zipCode} 
                      onChange={(e) => { 
                        formik.handleChange(e); 
                        formik.setFieldTouched("zipCode", false)
                      }} 
                      label={'Zip Code'} 
                      customStyles={{margin: '16px 0 0 0', width: '232px'}} 
                      id="zipCode" 
                      name="zipCode"
                      isStatusDisplayed={!!formik.touched.zipCode} 
                      error={formik.errors.zipCode}
                    />
                    <TextBox 
                      value={formik.values.city} 
                      onChange={(e) => { 
                        formik.handleChange(e); 
                        formik.setFieldTouched("city", false)
                      }} 
                      label={'City'} 
                      customStyles={{margin: '16px 0 0 10px', width: '353px'}} 
                      id="city" 
                      name="city"
                      isStatusDisplayed={!!formik.touched.city} 
                      error={formik.errors.city}
                    />
                  </div>

                  <TextBox 
                    value={formik.values.state} 
                    onChange={(e) => { 
                      formik.handleChange(e); 
                      formik.setFieldTouched("state", false)
                    }} 
                    placeholder={'STATE'} 
                    customStyles={{margin: '38px 0 0 0'}} 
                    id="state" 
                    name="state"
                    isStatusDisplayed={!!formik.touched.state} 
                    error={formik.errors.state}
                  />
                  <CustomCombobox 
                    currentValue={formik.values.country} 
                    placeholder={`Country`} 
                    options={filteredCountries} 
                    customStyles={{margin: '38px 0 0 0'}} 
                    id={`country`} 
                    name={`country`} 
                    onSelect={(newValue) => {
                      formik.setFieldValue(`country`, newValue, true);  
                      formik.setFieldTouched(`country`, false)
                    }} 
                    error={formik.errors.country}
                    isStatusDisplayed={!!formik.touched.country} 
                    isValidated={true}
                  />

                  <div className='multiple-row-input'>
                    <TextBox 
                      value={formik.values.prefixPhoneNumber} 
                      onChange={(e) => { 
                        formik.handleChange(e); 
                        formik.setFieldTouched("prefixPhoneNumber", false)
                      }} 
                      placeholder={'PREFIX'} 
                      customStyles={{margin: '38px 0 0 0', width: '121px'}} 
                      id="prefixPhoneNumber" 
                      name="prefixPhoneNumber"
                      isStatusDisplayed={!!formik.touched.prefixPhoneNumber} 
                      error={formik.errors.prefixPhoneNumber}
                    />
                    <TextBox 
                      value={formik.values.phoneNumber} 
                      onChange={(e) => { 
                        formik.handleChange(e); 
                        formik.setFieldTouched("phoneNumber", false)
                      }} 
                      label={'Telephone Number'} 
                      customStyles={{margin: '16px 0 0 10px', width: '463px'}} 
                      id="phoneNumber" 
                      name="phoneNumber"
                      isStatusDisplayed={!!formik.touched.phoneNumber} 
                      error={formik.errors.phoneNumber}
                    />
                  </div>

                  <div className='item-sizes-block'>
                    {formik.values.sizes.map((value, index) => (
                      <CustomCombobox 
                        currentValue={value} 
                        placeholder={`SIZE ${index + 1}`} 
                        options={sizes} 
                        customStyles={{margin: '0', width: '111px'}} 
                        id={`size-${index + 1}`} 
                        name={`sizes[${index}]`} 
                        onSelect={(newValue) => {
                          console.log(formik.values);
                          formik.setFieldValue(`sizes[${index}]`, newValue, true);  
                          formik.setFieldTouched(`sizes[${index}]`, false)
                        }} 
                        isTinyText='true' 
                        error={formik.errors.sizes}
                        isStatusDisplayed={formik.touched.sizes && formik.touched.sizes[index]} 
                        isValidated={true}
                      />
                    ))}
                  </div>
                </Form>
              </div>
            </Formik>
          </div>

          <Formik {...checkBoxesFormik}>
            <div className='shipping-confirm-block'>
              <Form>
                <CustomCheckbox 
                  checked={checkBoxesFormik.values.agreeTerms} 
                  onChange={(e) => { 
                    checkBoxesFormik.handleChange(e); 
                    checkBoxesFormik.setFieldTouched("agreeTerms", false)
                  }} 
                  name="agreeTerms" 
                  label={
                    <p>
                      I agree that I have read the <a href="https://www.lvlcs.io/terms-conditions" target="_blank" rel="noreferrer">terms of condition</a> & <a href="https://www.lvlcs.io/privacy-policy" target="_blank" rel="noreferrer">privacy policy</a>.
                    </p>
                  }
                  isStatusDisplayed={!!checkBoxesFormik.touched.agreeTerms} 
                  error={checkBoxesFormik.errors.agreeTerms}
                />
                <CustomCheckbox 
                  checked={checkBoxesFormik.values.confirmSizing} 
                  onChange={(e) => { 
                    checkBoxesFormik.handleChange(e); 
                    checkBoxesFormik.setFieldTouched("confirmSizing", false)
                  }} 
                  name="confirmSizing" 
                  label={
                  <p>
                    I have confirmed my sizing and aware that all sales are final and can not exchange or return the product.
                  </p>
                  }
                  isStatusDisplayed={!!checkBoxesFormik.touched.confirmSizing} 
                  error={checkBoxesFormik.errors.confirmSizing}
                />
              </Form>
            </div>
          </Formik>
        </div>

        <div className='navigation-block-buttons'>
          <ButtonFilled 
            label='NEXT' 
            palette='primary' 
            size='regular' 
            customStyles={{width: 269.5}} 
            onClick={onSubmitShippingInfo}
          />
        </div>
      </div>
    </section>
  );
};

export default ShippingInformationPage;